// Waiting-state messaging for the chat.
//
// WHY THIS EXISTS
//
// Enabling adaptive thinking made Lantern noticeably slower to first word,
// because it now works the problem out before it starts writing. That is the
// point — it is what stopped the wrong answers — but it leaves a child
// watching three dots for a lot longer than before. An 8-year-old reads a
// long silence as "it's broken" and taps again.
//
// Streaming the answer as it generates is NOT the fix here, for two reasons.
// It would break the mandatory safeguarding scan, which has to see a complete
// response before any of it reaches a child. And it would not even help much:
// most of the wait is thinking tokens, which are filtered out and never shown,
// so a streaming child would watch the same blank screen.
//
// So instead of pretending to show content we do not have, we say honestly
// that Lantern is still working, and say it in a way that gets more
// reassuring the longer it takes.
//
// HONESTY RULE
//
// These messages claim only what the client can actually observe: a request
// is in flight and this much time has passed. None of them claims to know
// which internal phase Lantern is in — the client cannot see that, and
// inventing a plausible-sounding phase would be a lie told to a child.
//
// CommonJS so the server jest suite can require it directly, matching
// utils/sessionTimer.js and utils/accessibility.js.

// Ordered by the elapsed time at which each becomes true. The first entry is
// the silent one: for a fast answer the child should just see the dots and
// then the reply, with no commentary at all.
const STAGES = [
  { afterMs: 0,      label: null },
  { afterMs: 4000,   label: 'Thinking about this one…' },
  { afterMs: 10000,  label: 'Still working it out…' },
  { afterMs: 20000,  label: 'This one needs a bit more thought…' },
  { afterMs: 35000,  label: 'Almost there — thanks for waiting.' },
];

/**
 * The message to show for a request that has been in flight for `elapsedMs`.
 * Returns null while the wait is short enough not to need explaining.
 */
function thinkingMessage(elapsedMs) {
  const ms = Number.isFinite(elapsedMs) && elapsedMs > 0 ? elapsedMs : 0;
  let label = null;
  for (const stage of STAGES) {
    if (ms >= stage.afterMs) label = stage.label;
  }
  return label;
}

/**
 * How far through the *reassurance* sequence we are, 0 to 1.
 *
 * Deliberately NOT a progress bar toward completion — we have no idea when
 * the answer will arrive, and a bar that implies we do would be dishonest and
 * would stall near the end, which is worse than no bar at all. This drives a
 * gentle indeterminate indicator instead, and is capped so it never reads as
 * "nearly finished".
 */
function thinkingProgressRatio(elapsedMs) {
  const ms = Number.isFinite(elapsedMs) && elapsedMs > 0 ? elapsedMs : 0;
  const last = STAGES[STAGES.length - 1].afterMs;
  return Math.min(ms / last, 1);
}

module.exports = { STAGES, thinkingMessage, thinkingProgressRatio };
